// src/pages/TeamAdminPage.tsx
import { useEffect, useState } from 'react'
import { secureFetch } from '@/utils/SecureFetch'
import { useNavigate, useParams } from 'react-router-dom'
import { Loading } from '@/components/ui/Loading'
import { Button } from '@/components/ui/Button'
import { ArrowLeft } from 'lucide-react'
import './team-admin-page.css'

interface MemberSummary {
	user_id: string
	user_name: string
	user_email: string
	last_active: string | null
	total_time_seconds: number
	total_actions: number
}

interface MemberStats {
	total_time_seconds: number
	session_count: number
	plays_viewed: number
	plays_edited: number
}

interface ActivityItem {
	id: string
	event_type: string
	entity_type: string | null
	entity_id: number | null
	duration_seconds: number | null
	created_at: string
}

function formatDuration(seconds: number): string {
	const hours = Math.floor(seconds / 3600)
	const minutes = Math.floor((seconds % 3600) / 60)
	if (hours > 0) {
		return `${hours}h ${minutes}m`
	}
	return `${minutes}m`
}

function formatRelativeTime(dateStr: string | null): string {
	if (!dateStr) return 'Never'
	const date = new Date(dateStr)
	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffMins = Math.floor(diffMs / 60000)
	const diffHours = Math.floor(diffMs / 3600000)
	const diffDays = Math.floor(diffMs / 86400000)

	if (diffMins < 60) return `${diffMins} min ago`
	if (diffHours < 24) return `${diffHours} hours ago`
	if (diffDays === 1) return 'Yesterday'
	return `${diffDays} days ago`
}

function formatEventType(eventType: string): string {
	const map: Record<string, string> = {
		play_open: 'Viewed play',
		play_close: 'Viewed play',
		play_edit: 'Edited play',
		playbook_open: 'Opened playbook',
		playbook_close: 'Viewed playbook',
		document_open: 'Opened document',
		document_close: 'Viewed document',
	}
	return map[eventType] || eventType
}

export function TeamAdminPage() {
	const { teamId } = useParams<{ teamId: string }>()
	const navigate = useNavigate()
	const [members, setMembers] = useState<MemberSummary[]>([])
	const [selectedMember, setSelectedMember] = useState<string | null>(null)
	const [memberStats, setMemberStats] = useState<MemberStats | null>(null)
	const [memberActivity, setMemberActivity] = useState<ActivityItem[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		async function fetchMembers() {
			const res = await secureFetch(`/api/teams/${teamId}/admin/members`)
			if (res.status === 401) {
				navigate('/login')
				return
			}
			if (res.status === 403) {
				setError('Access denied. Only team owners can view this page.')
				setLoading(false)
				return
			}
			const data = await res.json()
			setMembers(data.members)
			setLoading(false)
		}

		fetchMembers()
	}, [teamId, navigate])

	useEffect(() => {
		if (!selectedMember) {
			setMemberStats(null)
			setMemberActivity([])
			return
		}

		async function fetchMemberDetail() {
			const res = await secureFetch(
				`/api/teams/${teamId}/admin/members/${selectedMember}/activity`,
			)
			if (res.ok) {
				const data = await res.json()
				setMemberStats(data.stats)
				setMemberActivity(data.activity)
			}
		}

		fetchMemberDetail()
	}, [teamId, selectedMember])

	if (loading) return <Loading />
	if (error) return <div className="team-admin-page"><p>{error}</p></div>

	const selectedMemberData = members.find(m => m.user_id === selectedMember)

	return (
		<div className="team-admin-page">
			<div className="team-admin-header">
				<div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
					<Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
						<ArrowLeft size={20} />
					</Button>
					<h1>Team Activity</h1>
				</div>
			</div>

			<div className="member-list">
				<div className="member-row header">
					<div>Member</div>
					<div>Last Active</div>
					<div>Time (7d)</div>
					<div>Actions</div>
				</div>
				{members.map(member => (
					<div
						key={member.user_id}
						className="member-row"
						onClick={() => setSelectedMember(
							selectedMember === member.user_id ? null : member.user_id,
						)}
					>
						<div>
							<div className="member-name">{member.user_name}</div>
							<div className="member-email">{member.user_email}</div>
						</div>
						<div>{formatRelativeTime(member.last_active)}</div>
						<div>{formatDuration(member.total_time_seconds)}</div>
						<div>{member.total_actions} edits</div>
					</div>
				))}
			</div>

			{selectedMember && memberStats && selectedMemberData && (
				<div className="member-detail-panel">
					<h2>{selectedMemberData.user_name} - Last 7 Days</h2>

					<div className="stats-grid">
						<div className="stat-card">
							<div className="stat-value">
								{formatDuration(memberStats.total_time_seconds)}
							</div>
							<div className="stat-label">Total Time</div>
						</div>
						<div className="stat-card">
							<div className="stat-value">{memberStats.session_count}</div>
							<div className="stat-label">Sessions</div>
						</div>
						<div className="stat-card">
							<div className="stat-value">{memberStats.plays_viewed}</div>
							<div className="stat-label">Plays Viewed</div>
						</div>
						<div className="stat-card">
							<div className="stat-value">{memberStats.plays_edited}</div>
							<div className="stat-label">Plays Edited</div>
						</div>
					</div>

					<div className="activity-list">
						<h3>Recent Activity</h3>
						{memberActivity.slice(0, 20).map(item => (
							<div key={item.id} className="activity-item">
                                <span className="activity-description">
                                    {formatEventType(item.event_type)}
									{item.duration_seconds && ` (${formatDuration(item.duration_seconds)})`}
                                </span>
								<span className="activity-time">
                                    {formatRelativeTime(item.created_at)}
                                </span>
							</div>
						))}
						{memberActivity.length === 0 && (
							<p style={{ color: 'var(--muted-foreground)' }}>
								No activity in the last 7 days
							</p>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
